import { Box, Skeleton, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { OrderStatuses } from 'entities/Order/model/consts/orderStatuses';
import { getCanEditCountOfQty } from 'entities/Order/model/selectors/ordersPermissions';
import {
    getSelectedOrderIsLoading,
    getSelectedOrderIsPreSubmission,
} from 'entities/Order/model/selectors/ordersSelectors';
import { UserRole, getUserRole } from 'entities/User';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { SavingsSummaryProductDetailsDrawer } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductDetailsDrawer';

import { OrderGroupedItemsTableRow } from './OrderGroupedItemsTableRow';

interface OrderGroupedItemsContainerProps {
    items: CatalogItem[];
    orderStatus: OrderStatuses;
}

export const OrderGroupedItemsTable = ({
    items,
    orderStatus,
}: OrderGroupedItemsContainerProps) => {
    const userRole = useSelector(getUserRole);
    const canSeePrices = useSelector(getUserCanSeePrices);
    const isLoading = useSelector(getSelectedOrderIsLoading);
    const isEditCountOfQty = useSelector(getCanEditCountOfQty);
    const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);

    const isInReview = orderStatus === OrderStatuses.IN_REVIEW;
    const isHidePrice = userRole === UserRole.MAINTENANCE_TECHNICIAN;

    const showPicker = isPreSubmission && isEditCountOfQty;

    const canSeeInventory =
        userRole !== UserRole.MAINTENANCE_TECHNICIAN && isPreSubmission;

    const { t } = useTranslation();

    const [productDetails, setProductDetails] = useState<{
        sku: string;
        initialItem: CatalogItem;
    } | null>(null);

    const handleOpenProductDetails = useCallback((rowItem: CatalogItem) => {
        setProductDetails({
            sku: rowItem.sku,
            initialItem: rowItem,
        });
    }, []);

    const handleCloseProductDetails = useCallback(() => {
        setProductDetails(null);
    }, []);

    return !isLoading ? (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    padding: '12px 21px 10px 24px',
                    height: '38px',
                    boxSizing: 'border-box',
                }}
            >
                <Box sx={{ flex: '52%' }}>
                    <Typography
                        variant="caption"
                        data-testclass="order-item-title-label"
                        sx={{ fontWeight: 600 }}
                    >
                        {t('Item')}
                    </Typography>
                </Box>
                {!isInReview && (
                    <>
                        {canSeeInventory && (
                            <Box
                                sx={{
                                    width: '70px',
                                    minWidth: '70px',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    data-testclass="order-items-qty-label"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {t('Stock')}
                                </Typography>
                            </Box>
                        )}
                        <Box
                            sx={{
                                width: showPicker ? '120px' : '50px',
                                minWidth: showPicker ? '120px' : '50px',
                                marginLeft: '40px',
                                display: 'flex',
                                justifyContent: showPicker
                                    ? 'center'
                                    : 'flex-end',
                            }}
                        >
                            <Typography
                                variant="caption"
                                data-testclass="order-items-qty-label"
                                sx={{ fontWeight: 600 }}
                            >
                                {isPreSubmission ? t('Qty') : t('Avail')}
                            </Typography>
                        </Box>
                        {canSeePrices && !isHidePrice && (
                            <Box
                                sx={{
                                    width: '100px',
                                    minWidth: '100px',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    data-testclass="order-items-total-label"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {t('Total')}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
            <Box>
                {items.map((item) => (
                    <OrderGroupedItemsTableRow
                        key={item.id}
                        item={item}
                        onOpenProductDetails={handleOpenProductDetails}
                    />
                ))}
            </Box>

            <SavingsSummaryProductDetailsDrawer
                sku={productDetails?.sku ?? null}
                open={productDetails !== null}
                onClose={handleCloseProductDetails}
                initialItem={productDetails?.initialItem ?? null}
                forceApiFetch
            />
        </Box>
    ) : (
        <Box display="flex" flexDirection="column" gap="8px" px="12px">
            {[0, 1, 2].map((i) => (
                <Skeleton
                    key={i}
                    variant="rectangular"
                    height={72}
                    sx={{ borderRadius: '4px' }}
                />
            ))}
        </Box>
    );
};
